import { ArrowLeft, Cuboid, Train, ShieldCheck, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

import SEO from "@/components/site/SEO";
import CTASection from "@/components/site/CTASection";

export default function VRTraction() {
  const kuulaUrl =
    "https://kuula.co/share/collection/7TmJc?logo=1&info=0&fs=1&vr=1&sd=1&initload=0&thumbs=1";

  return (
    <>
      <SEO
        title="VR Traction Course Example | TRAC-Learn"
        description="Explore an example VR traction training course using immersive 360° learning to support railway traction familiarisation, equipment awareness and safety-critical development."
        path="/vr-traction"
      />

      <section className="relative overflow-hidden bg-navy py-20 md:py-28 text-white">
        <div className="container-rail">
          <Link
            to="/for-organisations"
            className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to TRAC
          </Link>

          <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_360px] lg:items-center">
            <div>
              <p className="label-eyebrow text-white/70">TRAC-Learn example</p>

              <h1 className="mt-4 font-display font-bold text-4xl md:text-6xl leading-tight text-white">
                VR Traction Course
              </h1>

              <p className="mt-6 max-w-3xl text-lg md:text-xl leading-relaxed text-white/80">
                This example shows how immersive 360° and VR learning can be
                used to support railway traction familiarisation, equipment
                awareness, cab layout understanding and safety-critical
                knowledge development.
              </p>

              <div className="mt-8 grid gap-3 sm:grid-cols-3 max-w-3xl">
                {["360° learning", "Cab familiarisation", "Evidence-based CPD"].map(
                  (item) => (
                    <div
                      key={item}
                      className="rounded-md border border-white/10 bg-white/5 px-4 py-3 text-sm font-bold text-white/90"
                    >
                      {item}
                    </div>
                  )
                )}
              </div>
            </div>

            <div className="rounded-md border border-white/10 bg-white/5 p-6 shadow-sm">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-md bg-white/10">
                <Cuboid className="h-7 w-7 text-white" />
              </div>

              <h2 className="mt-5 font-display font-bold text-2xl leading-tight">
                Immersive traction familiarisation
              </h2>

              <p className="mt-4 text-white/80 leading-relaxed">
                A controlled way to introduce learners to traction environments
                before, during or after practical training activity.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white">
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">Interactive example</p>

            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              Explore the VR traction course environment.
            </h2>

            <p className="mt-4 text-slate-600 leading-relaxed">
              The embedded example below demonstrates how a learner could move
              through a traction learning environment, view key areas and build
              familiarity with the cab or equipment layout using immersive
              content.
            </p>
          </div>

          <div className="mt-10 overflow-hidden rounded-md border border-slate-200 bg-slate-50 shadow-sm">
            <iframe
              title="VR Traction Course 360 Degree Example"
              src={kuulaUrl}
              width="100%"
              height="640"
              frameBorder="0"
              allow="xr-spatial-tracking; gyroscope; accelerometer; fullscreen"
              allowFullScreen
              scrolling="no"
              className="block w-full min-h-[640px]"
            />
          </div>

          <div className="mt-4 rounded-md border border-slate-200 bg-white p-4 text-sm text-slate-600">
            If the embedded example does not load on your device,{" "}
            <a
              href={kuulaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-safety hover:underline"
            >
              open the VR traction example in a new tab
            </a>
            .
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-slate-50">
        <div className="container-rail">
          <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
            <div>
              <p className="label-eyebrow text-safety">Training value</p>

              <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
                Supporting traction knowledge before practical exposure.
              </h2>

              <p className="mt-5 text-slate-600 leading-relaxed">
                VR traction learning is not intended to replace formal traction
                training, practical handling, assessment or employer competence
                arrangements. Its value is in preparation, reinforcement,
                familiarisation and structured evidence of learning activity.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {[
                {
                  icon: Train,
                  title: "Cab familiarisation",
                  body:
                    "Help learners identify controls, equipment, displays and key traction-specific features before live exposure.",
                },
                {
                  icon: ShieldCheck,
                  title: "Risk-free environment",
                  body:
                    "Allow learners to explore and revisit learning points without operational pressure or live railway risk.",
                },
                {
                  icon: CheckCircle2,
                  title: "Knowledge reinforcement",
                  body:
                    "Use immersive content to support classroom learning, eLearning modules, briefings and instructor-led discussion.",
                },
                {
                  icon: Cuboid,
                  title: "Scenario potential",
                  body:
                    "Build towards fault scenarios, equipment identification, degraded working prompts and reflective learning tasks.",
                },
              ].map((item, index) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.title}
                    className="rounded-md border border-slate-200 bg-white p-6 shadow-sm"
                    data-testid={`vr-traction-benefit-${index}`}
                  >
                    <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-navy text-white">
                      <Icon className="h-5 w-5" />
                    </div>

                    <h3 className="mt-4 font-display font-bold text-lg text-navy">
                      {item.title}
                    </h3>

                    <p className="mt-2 text-sm leading-relaxed text-slate-600">
                      {item.body}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="TRAC-Learn"
        title="Discuss immersive railway training."
        intro="TRAC-Learn can support VR, 360° learning, eLearning, CPD, operational briefings and evidence-based training delivery for railway organisations."
        primary={{
          label: "Discuss TRAC-Learn",
          to: "/contact",
          testId: "vr-traction-cta-contact",
        }}
        secondary={{
          label: "Back to TRAC",
          to: "/for-organisations",
          testId: "vr-traction-cta-trac",
        }}
      />
    </>
  );
}